
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PasswordInput } from "@/components/ui/password-input";
import { PasswordStrengthMeter } from "@/components/ui/password-strength-meter";
import { PasswordRules } from "@/components/ui/password-rules";
import { usePasswordValidation } from "@/hooks/usePasswordValidation";
import { useToast } from "@/components/ui/use-toast";
import { AlertTriangle } from 'lucide-react';
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from 'react-i18next';
import { supabase } from "@/integrations/supabase/client";
import { useCommunity } from '@/contexts/CommunityContext';

const ResetPassword = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasValidSession, setHasValidSession] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { refreshSession } = useAuth();
  const { t } = useTranslation();
  const { currentCommunity } = useCommunity();

  const passwordValidation = usePasswordValidation({ enableConfirmPassword: true });

  useEffect(() => {
    const checkRecoverySession = async () => {
      try {
        console.log("Checking for recovery session...");
        
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Session check error:", error);
          setSessionError("Unable to verify your reset session. Please request a new password reset link.");
          setLoading(false);
          return;
        }
        
        if (session && session.user) {
          console.log("Valid recovery session found");
          setHasValidSession(true);
          setLoading(false);
          return;
        }
        
        console.error("No valid recovery session found");
        setSessionError("Your reset link has expired or is invalid. Please request a new one.");
        setLoading(false);
      } catch (error) {
        console.error("Error during recovery session check:", error);
        setSessionError("An error occurred while processing your reset link. Please request a new one.");
        setLoading(false);
      }
    };
    
    checkRecoverySession();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!hasValidSession) {
      toast({
        variant: "destructive",
        title: "Invalid session",
        description: "No valid reset session found. Please request a new password reset link.",
      });
      return;
    }
    
    if (passwordValidation.password !== passwordValidation.confirmPassword) {
      toast({
        variant: "destructive",
        title: "Passwords don't match",
        description: "Please make sure your passwords match.",
      });
      return;
    }
    
    if (!passwordValidation.isValid) {
      toast({
        variant: "destructive",
        title: "Password too weak",
        description: "Password must meet at least 3 of the security requirements.",
      });
      return;
    }
    
    setIsProcessing(true);
    
    try {
      console.log("Attempting to update password...");
      
      const { error } = await supabase.auth.updateUser({
        password: passwordValidation.password
      });
      
      if (error) {
        console.error("Password update error:", error);
        throw error;
      }
      
      console.log("Password updated successfully");
      
      toast({
        title: "Password updated successfully",
        description: "You will be redirected to the homepage shortly.",
      });
      
      // Refresh the session to ensure the user is properly authenticated
      await refreshSession();
      
      // Wait a moment for the session to be updated, then redirect
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 2000);
      
    } catch (error: any) {
      console.error("Password update error:", error);
      
      toast({
        variant: "destructive",
        title: "Error updating password",
        description: error.message || "Failed to update password. The reset link may have expired. Please request a new one.",
      });
      
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-[42%_58%]">
      {/* Left Panel - Brand Section */}
      <div className="hidden md:flex bg-[hsl(var(--primary)/0.03)] relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-20 w-32 h-32 bg-primary/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-40 right-20 w-48 h-48 bg-primary/8 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-10 w-24 h-24 bg-primary/6 rounded-full blur-2xl"></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="space-y-6">
              <h2 className="text-4xl lg:text-5xl font-bold text-foreground leading-tight">
                Welcome Back to
                <br />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/80">
                  {currentCommunity?.name}
                </span>
              </h2>
              
              <p className="text-lg text-muted-foreground leading-relaxed max-w-sm">
                Connect with your local community and discover amazing services near you.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right Panel - Form Section */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-background">
        <div className="w-full max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Card className="rounded-2xl shadow-lg border bg-card">
              <CardHeader className="space-y-4 text-center p-8 pb-6">
                {/* Mobile Brand Header */}
                <div className="md:hidden space-y-2">
                  <span className="text-lg font-semibold text-foreground">
                    {currentCommunity?.name}
                  </span>
                  <p className="text-sm text-muted-foreground">
                    Reset your password
                  </p>
                </div>
                <CardTitle className="text-2xl font-bold">{t('reset.password.title')}</CardTitle>
                <CardDescription>
                  {t('reset.password.description')}
                </CardDescription>
              </CardHeader>
              
              <div className="px-8 pb-8">
                {loading ? (
                  <div className="flex justify-center p-6">
                    <div className="h-8 w-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin"></div>
                  </div>
                ) : sessionError ? (
                  <div>
                    <Alert variant="destructive" className="mb-4">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{sessionError}</AlertDescription>
                    </Alert>
                    <div className="text-center mt-4">
                      <Button 
                        variant="outline" 
                        onClick={() => navigate('/auth/request-reset-password')}
                      >
                        {t('reset.password.request')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="newPassword">{t('reset.password.new')}</Label>
                        <PasswordInput
                          id="newPassword"
                          value={passwordValidation.password}
                          onChange={(e) => passwordValidation.setPassword(e.target.value)}
                          onFocus={() => {
                            passwordValidation.setPasswordFocused(true);
                            passwordValidation.setPasswordTouched(true);
                          }}
                          onBlur={() => {
                            passwordValidation.setPasswordFocused(false);
                          }}
                          required
                          className="focus:border-primary/50"
                          placeholder="••••••••"
                          disabled={isProcessing}
                        />
                        
                        <PasswordStrengthMeter 
                          password={passwordValidation.password} 
                          strength={passwordValidation.passwordStrength} 
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword">{t('reset.password.confirm')}</Label>
                        <PasswordInput
                          id="confirmPassword"
                          value={passwordValidation.confirmPassword}
                          onChange={(e) => passwordValidation.setConfirmPassword(e.target.value)}
                          required
                          className="focus:border-primary/50"
                          placeholder="••••••••"
                          disabled={isProcessing}
                        />
                        {passwordValidation.confirmPassword && passwordValidation.password !== passwordValidation.confirmPassword && (
                          <p className="text-xs text-red-500 mt-1">{t('reset.password.mismatch')}</p>
                        )}
                      </div>

                      <PasswordRules
                        shouldShow={passwordValidation.shouldShowRules}
                        rules={passwordValidation.passwordRules}
                        showPasswordsMatch={passwordValidation.confirmPassword !== ''}
                      />
                    </div>
                    
                    <div className="mt-6">
                      <Button 
                        type="submit" 
                        className="w-full" 
                        disabled={!hasValidSession || isProcessing || !passwordValidation.isValid}
                      >
                        {isProcessing ? t('reset.password.updating') : t('reset.password.update')}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
